import {
  Box,
  Button,
  Card,
  CardActions,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useState } from "react";
import {
  email,
  Form,
  PasswordInput,
  SelectInput,
  required,
  TextInput,
  useNotify,
} from "react-admin";
import { Link, useLocation, useNavigate } from "react-router-dom";
import sendRequest from "../dataProvider/sendRequest";

interface FormValues {
  institution_name: string;
  institution_website: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  zip: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password: string;
}

const requiredField = required("This field is required");
const emailField = email("Enter a valid email address");
const passwordRuleText =
  "Minimum 8 characters with at least 1 letter, 1 number, and 1 special character";
const passwordPattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/;

const US_STATE_CHOICES = [
  { id: "AK", name: "Alaska" },
  { id: "AL", name: "Alabama" },
  { id: "AR", name: "Arkansas" },
  { id: "AZ", name: "Arizona" },
  { id: "CA", name: "California" },
  { id: "CO", name: "Colorado" },
  { id: "CT", name: "Connecticut" },
  { id: "DC", name: "District Of Columbia" },
  { id: "DE", name: "Delaware" },
  { id: "FL", name: "Florida" },
  { id: "GA", name: "Georgia" },
  { id: "HI", name: "Hawaii" },
  { id: "IA", name: "Iowa" },
  { id: "ID", name: "Idaho" },
  { id: "IL", name: "Illinois" },
  { id: "IN", name: "Indiana" },
  { id: "KS", name: "Kansas" },
  { id: "KY", name: "Kentucky" },
  { id: "LA", name: "Louisiana" },
  { id: "MA", name: "Massachusetts" },
  { id: "MD", name: "Maryland" },
  { id: "ME", name: "Maine" },
  { id: "MI", name: "Michigan" },
  { id: "MN", name: "Minnesota" },
  { id: "MO", name: "Missouri" },
  { id: "MS", name: "Mississippi" },
  { id: "MT", name: "Montana" },
  { id: "NC", name: "North Carolina" },
  { id: "ND", name: "North Dakota" },
  { id: "NE", name: "Nebraska" },
  { id: "NH", name: "New Hampshire" },
  { id: "NJ", name: "New Jersey" },
  { id: "NM", name: "New Mexico" },
  { id: "NV", name: "Nevada" },
  { id: "NY", name: "New York" },
  { id: "OH", name: "Ohio" },
  { id: "OK", name: "Oklahoma" },
  { id: "OR", name: "Oregon" },
  { id: "PA", name: "Pennsylvania" },
  { id: "RI", name: "Rhode Island" },
  { id: "SC", name: "South Carolina" },
  { id: "SD", name: "South Dakota" },
  { id: "TN", name: "Tennessee" },
  { id: "TX", name: "Texas" },
  { id: "UT", name: "Utah" },
  { id: "VA", name: "Virginia" },
  { id: "VT", name: "Vermont" },
  { id: "WA", name: "Washington" },
  { id: "WI", name: "Wisconsin" },
  { id: "WV", name: "West Virginia" },
  { id: "WY", name: "Wyoming" },
];

const validateWebsite = (value: string) => {
  if (!value) {
    return "This field is required";
  }

  const websitePattern =
    /^(https?:\/\/)?([a-z\d]([a-z\d-]*[a-z\d])*\.)+[a-z]{2,}(\/.*)?$/i;

  if (!websitePattern.test(value)) {
    return "Enter a valid website URL";
  }

  return undefined;
};

const validateZip = (value: string) => {
  if (!value) {
    return "This field is required";
  }

  if (!/^\d{5}(-\d{4})?$/.test(value)) {
    return "Enter a valid ZIP code";
  }

  return undefined;
};

const validateConfirmPassword = (value: string, allValues: FormValues) => {
  if (!value) {
    return "Confirm Password is required";
  }

  if (value !== allValues?.password) {
    return "Passwords do not match";
  }

  return undefined;
};

const Register = () => {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = React.useState("");
  const [agreementAccepted, setAgreementAccepted] = React.useState(false);
  const [agreementError, setAgreementError] = React.useState("");
  const [passwordValue, setPasswordValue] = React.useState("");
  const [passwordInteracted, setPasswordInteracted] = React.useState(false);
  const notify = useNotify();
  const navigate = useNavigate();
  const location = useLocation();
  const registerType = React.useMemo(
    () => new URLSearchParams(location.search).get("sRegisterType") || "",
    [location.search]
  );

  const passwordRules = {
    minLength: passwordValue.length >= 8,
    hasLetter: /[A-Za-z]/.test(passwordValue),
    hasNumber: /\d/.test(passwordValue),
    hasSpecial: /[^A-Za-z0-9\s]/.test(passwordValue),
  };

  const isPasswordValid = passwordPattern.test(passwordValue);

  const getPasswordRuleColor = (ruleValid: boolean) => {
    if (!passwordInteracted) {
      return "text.secondary";
    }

    return ruleValid ? "success.main" : "error.main";
  };

  const handleSubmit = async (values: any) => {
    const auth = values as FormValues;

    setPasswordValue(auth.password || "");
    setPasswordInteracted(true);

    if (!auth.password) {
      notify("Password is required", { type: "error" });
      return;
    }

    if (!passwordPattern.test(auth.password)) {
      notify(passwordRuleText, { type: "error" });
      return;
    }

    if (!agreementAccepted) {
      setAgreementError(
        "You must agree to the Trial Agreement, Privacy Policy and Security Policy"
      );
      notify(
        "You must agree to the Trial Agreement, Privacy Policy and Security Policy",
        { type: "error" }
      );
      return;
    }

    setAgreementError("");
    setLoading(true);

    const reqUrl = "registerBanker";
    const formJson = {
      institution_name: auth.institution_name,
      institution_website: auth.institution_website,
      sStreet: auth.address_line_1,
      sStreet2: auth.address_line_2 || "",
      sCity: auth.city,
      sState: auth.state,
      sZip: auth.zip,
      first_name: auth.first_name,
      last_name: auth.last_name,
      email: auth.email,
      password: auth.password,
      confirm_password: auth.confirm_password,
      registerType: registerType,
      agreementAccepted: agreementAccepted ? 1 : 0,
    };

    const response = await sendRequest(reqUrl, {
      id: 0,
      data: formJson,
    });

    if (response && (response.status == "200" || response.code == "200")) {
      setSuccessMessage(
        "Registration submitted successfully.Please check your email for verification link."
      );
      setLoading(false);
      // setTimeout(() => {
      //   navigate("/login");
      // }, 2000);
      return;
    } else if (
      response &&
      (response.status == "201" || response.code == "201")
    ) {
      notify("Registration failed", { type: "error" });
      setLoading(false);
      setSuccessMessage("");
      return;
    }

    if (response && response.message) {
      notify(response.message, { type: "error" });
    } else {
      notify("Registration failed", { type: "error" });
    }

    setLoading(false);
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          alignItems: "center",
          justifyContent: "flex-start",
          backgroundColor: "#f2f7fa",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          padding: "2em 1em 4em",
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 760,
            marginTop: "3em",
            padding: "2em",
            '& .MuiInputLabel-root span[aria-hidden="true"]': {
              display: "none",
            },
          }}
        >
          <Box
            sx={{
              margin: "1em",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img src="/images/happymango_logo.png" alt="logo" />
          </Box>

          <Typography
            variant="h5"
            sx={{ textAlign: "center", color: "#1f3558", fontWeight: 700 }}
          >
            Banker Registration
          </Typography>

          <Typography
            sx={{
              marginTop: "0.75em",
              textAlign: "center",
              color: (theme) => theme.palette.grey[600],
            }}
          >
            Create your Happy Mango institutional account.
          </Typography>

          {successMessage && (
            <>
              <Box
                sx={{
                  marginTop: "1.5em",
                  display: "flex",
                  justifyContent: "center",
                  color: (theme) => theme.palette.success.main,
                }}
              >
                {successMessage}
              </Box>
              <Box
                sx={{
                  textAlign: "center",
                  width: "100%",
                  mt: 1,
                }}
              >
                <Link
                  to="/login"
                  style={{
                    textDecoration: "none",
                    color: "#00CCB1",
                  }}
                >
                  Back to Login
                </Link>
              </Box>
            </>
          )}

          {!successMessage && (
            <>
              <Box sx={{ mt: 3, padding: "0 1em 1em 1em" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextInput
                      source="institution_name"
                      label="Institution Name"
                      variant="outlined"
                      className="registerform"
                      fullWidth
                      validate={requiredField}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextInput
                      source="institution_website"
                      label="Institution Website"
                      variant="outlined"
                      className="registerform"
                      fullWidth
                      validate={validateWebsite}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextInput
                      source="address_line_1"
                      label="Address line 1"
                      variant="outlined"
                      className="registerform"
                      fullWidth
                      validate={requiredField}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextInput
                      source="address_line_2"
                      label="Address line 2"
                      className="registerform"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextInput
                      source="city"
                      label="City"
                      className="registerform"
                      variant="outlined"
                      fullWidth
                      validate={requiredField}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <SelectInput
                      source="state"
                      label="State"
                      choices={US_STATE_CHOICES}
                      variant="outlined"
                      className="registerform"
                      fullWidth
                      validate={requiredField}
                      sx={{
                        "&": {
                          marginTop: "-2px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextInput
                      source="zip"
                      label="Zip"
                      className="registerform"
                      variant="outlined"
                      fullWidth
                      validate={validateZip}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextInput
                      source="first_name"
                      label="First name"
                      className="registerform"
                      variant="outlined"
                      fullWidth
                      validate={requiredField}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextInput
                      source="last_name"
                      label="Last name"
                      className="registerform"
                      variant="outlined"
                      fullWidth
                      validate={requiredField}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextInput
                      source="email"
                      label="Your Email"
                      className="registerform"
                      type="email"
                      variant="outlined"
                      fullWidth
                      validate={[requiredField, emailField]}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <PasswordInput
                      source="password"
                      label="Login Password"
                      variant="outlined"
                      fullWidth
                      helperText={false}
                      onChange={(event) => {
                        setPasswordValue(event.target.value);
                        setPasswordInteracted(true);
                      }}
                      onBlur={() => {
                        setPasswordInteracted(true);
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor:
                              passwordInteracted && !isPasswordValid
                                ? "error.main"
                                : "success.main",
                          },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor:
                              passwordInteracted && !isPasswordValid
                                ? "error.main"
                                : "success.main",
                          },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor:
                              passwordInteracted && !isPasswordValid
                                ? "error.main"
                                : "success.main",
                          },
                        "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor:
                              passwordInteracted && !isPasswordValid
                                ? "error.main"
                                : "success.main",
                          },
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        pl: 0.5,
                        whiteSpace: "nowrap",
                        overflowX: "auto",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: getPasswordRuleColor(passwordRules.minLength),
                        }}
                      >
                        Minimum 8 characters
                      </Box>
                      <Box
                        component="span"
                        sx={{ color: "text.secondary", mx: 0.75 }}
                      >
                        ,
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          color: getPasswordRuleColor(passwordRules.hasLetter),
                        }}
                      >
                        1 letter
                      </Box>
                      <Box
                        component="span"
                        sx={{ color: "text.secondary", mx: 0.75 }}
                      >
                        ,
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          color: getPasswordRuleColor(passwordRules.hasNumber),
                        }}
                      >
                        1 number
                      </Box>
                      <Box
                        component="span"
                        sx={{ color: "text.secondary", mx: 0.75 }}
                      >
                        ,
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          color: getPasswordRuleColor(passwordRules.hasSpecial),
                        }}
                      >
                        1 special character
                      </Box>
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <PasswordInput
                      source="confirm_password"
                      label="Confirm Password"
                      variant="outlined"
                      fullWidth
                      validate={validateConfirmPassword}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl error={Boolean(agreementError)}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={agreementAccepted}
                            onChange={(event) => {
                              setAgreementAccepted(event.target.checked);
                              if (event.target.checked) {
                                setAgreementError("");
                              }
                            }}
                            sx={{ alignSelf: "flex-start", mt: "2px" }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            By signing up, I agree to Happy Mango's Trial
                            Agreement and have read and understood Happy Mango's
                            Privacy Policy and Security Policy.
                          </Typography>
                        }
                        sx={{ alignItems: "flex-start", marginLeft: 0 }}
                      />
                      {agreementError && (
                        <FormHelperText>{agreementError}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <CardActions
                sx={{
                  padding: "0 1em 1em 1em",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={loading}
                  sx={{
                    minWidth: 220,
                    backgroundColor: "#00ccb1",
                    "&:hover": {
                      backgroundColor: "#00b39b",
                    },
                  }}
                >
                  {loading && <CircularProgress size={25} thickness={2} />}
                  Register
                </Button>
              </CardActions>
            </>
          )}
        </Card>
      </Box>
    </Form>
  );
};

export default Register;
